import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  api,
  ApiError,
  type SystemPrompt,
  type SystemPromptCostEstimate,
} from "../api.js";
import { useToken } from "../auth.js";
import { GroupButton } from "../components/GroupButton.js";

// System prompts admin: list active + archived in two tables; editor exposes an
// unlimited textarea plus a debounced per-model cost preview so the operator can
// see how expensive shipping the prompt is on every LLM call.
export function SystemPrompts() {
  const token = useToken();
  const { t, i18n } = useLingui();
  const [prompts, setPrompts] = useState<SystemPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<SystemPrompt | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listSystemPrompts(token);
      setPrompts(res.prompts);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const archive = async (p: SystemPrompt) => {
    if (
      !confirm(
        t`Archive prompt "${p.label}"? Any active model still using it must be reassigned first.`,
      )
    )
      return;
    setBusyId(p.id);
    try {
      await api.archiveSystemPrompt(token, p.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const unarchive = async (p: SystemPrompt) => {
    if (!confirm(t`Unarchive prompt "${p.label}"?`)) return;
    setBusyId(p.id);
    try {
      await api.unarchiveSystemPrompt(token, p.id);
      await refresh();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusyId(null);
    }
  };

  const activePrompts = useMemo(() => prompts.filter((p) => p.isActive), [prompts]);
  const archivedPrompts = useMemo(() => prompts.filter((p) => !p.isActive), [prompts]);

  const renderRow = (p: SystemPrompt) => (
    <tr key={p.id}>
      <td>{p.label}</td>
      <td>
        <span className="muted">{truncate(p.content, 120)}</span>
      </td>
      <td>{p.modelCount.toLocaleString(i18n.locale)}</td>
      <td>{new Date(p.updatedAt).toLocaleString(i18n.locale)}</td>
      <td>
        <GroupButton
          label={<Trans>Edit</Trans>}
          dropdownLabel={t`System prompt actions`}
          onClick={() => setEditing(p)}
        >
          <button className="link-btn" onClick={() => setEditing(p)}>
            <Trans>Edit</Trans>
          </button>
          {p.isActive ? (
            <button
              className="link-btn danger"
              disabled={busyId === p.id}
              onClick={() => void archive(p)}
            >
              <Trans>Archive</Trans>
            </button>
          ) : (
            <button
              className="link-btn"
              disabled={busyId === p.id}
              onClick={() => void unarchive(p)}
            >
              <Trans>Unarchive</Trans>
            </button>
          )}
        </GroupButton>
      </td>
    </tr>
  );

  const renderTable = (rows: SystemPrompt[]) => (
    <table className="table">
      <thead>
        <tr>
          <th>
            <Trans>Admin label</Trans>
          </th>
          <th>
            <Trans>Preview</Trans>
          </th>
          <th>
            <Trans>Models</Trans>
          </th>
          <th>
            <Trans>Updated at</Trans>
          </th>
          <th />
        </tr>
      </thead>
      <tbody>{rows.map(renderRow)}</tbody>
    </table>
  );

  return (
    <div>
      <header className="page-header">
        <h1>
          <Trans>System prompts</Trans>
        </h1>
        <button onClick={() => setCreating(true)}>
          <Trans>New prompt</Trans>
        </button>
      </header>
      {error && <div className="banner err">{error}</div>}
      {loading ? (
        <p className="muted">
          <Trans>Loading…</Trans>
        </p>
      ) : (
        <>
          {activePrompts.length === 0 ? (
            <p className="muted">
              <Trans>No active prompts yet.</Trans>
            </p>
          ) : (
            renderTable(activePrompts)
          )}
          {archivedPrompts.length > 0 && (
            <>
              <h2 style={{ marginTop: 24 }}>
                <Trans>Archived prompts</Trans>
              </h2>
              {renderTable(archivedPrompts)}
            </>
          )}
        </>
      )}

      {creating && (
        <SystemPromptEditor
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            void refresh();
          }}
        />
      )}
      {editing && (
        <SystemPromptEditor
          prompt={editing}
          onCancel={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}

function truncate(text: string, max: number): string {
  const collapsed = text.replace(/\s+/g, " ").trim();
  if (collapsed.length <= max) return collapsed;
  return collapsed.slice(0, max - 1) + "…";
}

function SystemPromptEditor({
  prompt,
  onCancel,
  onSaved,
}: {
  prompt?: SystemPrompt;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const token = useToken();
  const { t, i18n } = useLingui();
  const isEdit = !!prompt;
  const [label, setLabel] = useState(prompt?.label ?? "");
  const [content, setContent] = useState(prompt?.content ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [estimate, setEstimate] = useState<SystemPromptCostEstimate | null>(null);
  const [estimateError, setEstimateError] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);

  // Live per-model cost preview: rerun the estimator ~250ms after the operator
  // stops typing. Empty content still calls (returns 0 tokens); the endpoint is
  // cheap and it keeps the table consistent instead of flickering blank.
  useEffect(() => {
    const handle = setTimeout(() => {
      let cancelled = false;
      setEstimating(true);
      setEstimateError(null);
      api
        .estimateSystemPromptCost(token, content)
        .then((res) => {
          if (!cancelled) setEstimate(res);
        })
        .catch((err) => {
          if (!cancelled)
            setEstimateError(err instanceof ApiError ? err.message : String(err));
        })
        .finally(() => {
          if (!cancelled) setEstimating(false);
        });
      return () => {
        cancelled = true;
      };
    }, 250);
    return () => clearTimeout(handle);
  }, [content, token]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedLabel = label.trim();
    const trimmedContent = content.trim();
    if (trimmedLabel.length === 0) {
      setError(t`Admin label is required`);
      return;
    }
    if (trimmedContent.length === 0) {
      setError(t`Content is required`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (isEdit) {
        await api.updateSystemPrompt(token, prompt!.id, {
          label: trimmedLabel,
          content: trimmedContent,
        });
      } else {
        await api.createSystemPrompt(token, {
          label: trimmedLabel,
          content: trimmedContent,
        });
      }
      onSaved();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onCancel}>
      <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
        <h2>{isEdit ? t`Edit ${prompt!.label}` : t`New system prompt`}</h2>
        <form onSubmit={submit}>
          <label>
            <Trans>Admin label</Trans>
            <input
              required
              maxLength={200}
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder={t`Internal name — used to pick this prompt from the models editor.`}
            />
          </label>
          <label>
            <Trans>Content</Trans>
            <textarea
              rows={18}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t`Full system prompt text — no length limit.`}
            />
          </label>

          <section style={{ marginTop: 12 }}>
            <h3>
              <Trans>Estimated cost per model</Trans>
            </h3>
            <p className="muted" style={{ marginTop: 0 }}>
              <Trans>
                Tokens estimated at ~4 characters per token; cost uses each active model's
                current input tariff.
              </Trans>
            </p>
            {estimateError && <div className="banner err">{estimateError}</div>}
            {estimate ? (
              <table className="table">
                <thead>
                  <tr>
                    <th>
                      <Trans>Model</Trans>
                    </th>
                    <th>
                      <Trans>Tokens</Trans>
                    </th>
                    <th>
                      <Trans>Input $/Mtok</Trans>
                    </th>
                    <th>
                      <Trans>Cost per call</Trans>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {estimate.perModel.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="muted">
                        <Trans>No active model.</Trans>
                      </td>
                    </tr>
                  ) : (
                    estimate.perModel.map((m) => (
                      <tr key={m.modelId}>
                        <td>{m.displayName}</td>
                        <td>{estimate.tokens.toLocaleString(i18n.locale)}</td>
                        <td>
                          {m.priceInputPerMtok === null
                            ? "—"
                            : `$${m.priceInputPerMtok.toFixed(3)}`}
                        </td>
                        <td>
                          {m.costPerCall === null
                            ? "—"
                            : `$${m.costPerCall.toFixed(6)}`}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            ) : (
              estimating && (
                <p className="muted">
                  <Trans>Estimating…</Trans>
                </p>
              )
            )}
          </section>

          {error && <div className="banner err">{error}</div>}
          <div className="actions">
            <button type="button" className="ghost" onClick={onCancel}>
              <Trans>Cancel</Trans>
            </button>
            <button type="submit" disabled={submitting}>
              {submitting ? <Trans>Saving…</Trans> : <Trans>Save</Trans>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
